
import React, { useState, useEffect, FormEvent } from 'react';
import { dateToTimestamp } from './dateUtils';

type AddMemberFormProps = {
  onAdd: (member: {
    name: string;
    email: string;
    nfc_token_id?: string;
    password?: string;
    sport_ids?: number[];
    date_of_birth?: number;
    gender: string;
    join_date?: number;
    membership_type?: string;
    membership_status?: string;
  }) => void;
  onCancel: () => void;
  loading: boolean;
  accessToken?: string | null;
  serverError?: string;
};

const AddMemberForm: React.FC<AddMemberFormProps> = ({ onAdd, onCancel, loading, accessToken, serverError: propServerError }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nfcTokenId, setNfcTokenId] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [joinDate, setJoinDate] = useState('');
  const [membershipType, setMembershipType] = useState('');
  const [membershipStatus, setMembershipStatus] = useState('');
  const [serverError, setServerError] = useState('');
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [joinDateError, setJoinDateError] = useState(false);
  const [genderError, setGenderError] = useState(false);

  // Default join_date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setJoinDate(today);
  }, []);
  const [sports, setSports] = useState<{id: number, name: string}[]>([]);
  const [selectedSports, setSelectedSports] = useState<number[]>([]);

  useEffect(() => {
    // Fetch sports for multi-select
    fetch('/api/sports', {
      headers: { 
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
      }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setSports(data);
      });
  }, [accessToken]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setServerError('');
    setNameError(!name.trim());
    setEmailError(!email.trim());
    setDateError(!dateOfBirth);
    setJoinDateError(!joinDate);
    setGenderError(!gender);
    if (!name.trim() || !email.trim() || !dateOfBirth || !joinDate || !gender) {
      return;
    }
  onAdd({
    name,
    email,
    nfc_token_id: nfcTokenId && nfcTokenId.trim() !== '' ? nfcTokenId : undefined,
    password: password || undefined,
    sport_ids: selectedSports,
    date_of_birth: dateToTimestamp(dateOfBirth),
    gender,
    join_date: dateToTimestamp(joinDate),
    membership_type: membershipType,
    membership_status: membershipStatus,
  });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-xl w-full max-w-2xl relative">
        <h3 className="text-xl font-bold mb-4">Add New Member</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* Left column */}
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Name *</label>
              <input
                type="text"
                className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${nameError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
                value={name}
                onChange={e => { setName(e.target.value); setNameError(false); }}
                required
              />
              {nameError && <span className="text-red-500 text-xs mt-1">Name is required</span>}
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Email *</label>
              <input
                type="email"
                className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${emailError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailError(false); }}
                required
              />
              {emailError && <span className="text-red-500 text-xs mt-1">Email is required</span>}
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Password</label>
              <input
                type="password"
                className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Membership Status</label>
              <input
                type="text"
                className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={membershipStatus}
                onChange={e => setMembershipStatus(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">NFC Token ID</label>
              <input
                type="text"
                className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={nfcTokenId}
                onChange={e => setNfcTokenId(e.target.value)}
              />
            </div>
          </div>

          {/* Right column */}
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Date of Birth</label>
              <input
                type="date"
                className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${dateError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
                value={dateOfBirth}
                onChange={e => { setDateOfBirth(e.target.value); setDateError(false); }}
                required
              />
              {dateError && <span className="text-red-500 text-xs mt-1">Date of birth is required</span>}
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Gender *</label>
              <select
                className={`shadow border rounded py-2 px-3 text-gray-700 focus:outline-none ${genderError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
                value={gender}
                onChange={e => { setGender(e.target.value); setGenderError(false); }}
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {genderError && <span className="text-red-500 text-xs mt-1">Gender is required</span>}
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Join Date</label>
              <input
                type="date"
                className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${joinDateError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
                value={joinDate}
                onChange={e => { setJoinDate(e.target.value); setJoinDateError(false); }}
                required
              />
              {joinDateError && <span className="text-red-500 text-xs mt-1">Join date is required</span>}
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Membership Type</label>
              <select
                className="shadow border rounded py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                value={membershipType}
                onChange={e => setMembershipType(e.target.value)}
              >
                <option value="">Select</option>
                <option value="Associate">Associate</option>
                <option value="Senior">Senior</option>
                <option value="Junior">Junior</option>
                <option value="Life">Life</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-bold mb-1">Sports</label>
              <select
                multiple
                className="shadow border rounded py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                value={selectedSports.map(String)}
                onChange={e => {
                  const options = Array.from(e.target.selectedOptions).map(opt => Number(opt.value));
                  setSelectedSports(options);
                }}
              >
                {sports.map(sport => (
                  <option key={sport.id} value={sport.id}>{sport.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {(serverError || propServerError) && <div className="mb-4 text-red-600 font-semibold">{propServerError || serverError}</div>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-md"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Member'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddMemberForm;
